import type { QueryState } from "redux-query";
import type { UseDataInfo } from "QueryTypes";
import { useSelector } from "react-redux";
import type { EntitiesState, RootState } from "StoreTypes";
import { type RunMutation, useMutation, useRequest } from "redux-query-react";
import type { GameScorecard, ScorecardPayload } from "ScorecardModels";
import { useMemo } from "react";
import {
  getScorecardsBySearch,
  makeDismissAllScorecardsMutation,
} from "./scorecardLookupQueries";

export const useScorecardsBySearch = (
  facilityId: string,
  body: ScorecardPayload,
): UseDataInfo<GameScorecard[]> => {
  const safeQuery = useMemo(() => {
    if (!facilityId?.trim()) {
      console.log("facilityId is falsy or empty for scorecards search query");
      return null;
    }
    return getScorecardsBySearch(facilityId, body);
  }, [facilityId, body]);

  const [queryState, refreshScorecards] = useRequest(safeQuery ?? undefined);

  const scorecards = useSelector((state: RootState): GameScorecard[] => {
    if (!state.entities.scorecards?.byFacilityId?.[facilityId]) return [];
    return Object.values(
      state.entities?.scorecards?.byFacilityId?.[facilityId],
    ).sort(
      (a: GameScorecard, b: GameScorecard) =>
        new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf(),
    );
  });

  return [scorecards, queryState, refreshScorecards];
};

export const useDismissScorecards = (
  facilityId: string,
): [QueryState, RunMutation<EntitiesState>] => {
  const [dismissServiceRequestQueryState, dismissServiceRequest] = useMutation(
    () => makeDismissAllScorecardsMutation(facilityId),
  );

  return [dismissServiceRequestQueryState, dismissServiceRequest];
};
